import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";

/**
 * Cinematic hand-off from the landing "squeeze" moment into the app.
 * When the landing page navigates here it sets a sessionStorage flag; we
 * paint a full-screen golden veil that starts fully opaque (matching the
 * end-state of the landing overlay) and then dissolves with a slow ease
 * while the destination page settles behind it. The result reads as one
 * continuous shot instead of a route swap.
 */
export const LandingTransitionVeil = () => {
  const [active, setActive] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let flag: string | null = null;
    try {
      flag = sessionStorage.getItem("landing:transition");
      if (flag) sessionStorage.removeItem("landing:transition");
    } catch {}
    if (!flag) return;
    // Only honor recent flags (avoid stale ones from prior sessions).
    const ts = Number(flag);
    if (!Number.isFinite(ts) || Date.now() - ts > 4000) return;
    setActive(true);
    const id = window.setTimeout(() => setActive(false), 1250);
    return () => window.clearTimeout(id);
  }, [location.pathname]);

  return (
    <AnimatePresence>
      {active && (
        <>
          {/* Golden curtain — matches landing overlay's end color, then dissolves */}
          <motion.div
            key="veil"
            className="fixed inset-0 z-[80] pointer-events-none"
            initial={{ opacity: 1, scale: 1.06 }}
            animate={{ opacity: 0, scale: 1 }}
            transition={{
              opacity: { duration: 1.15, ease: [0.4, 0, 0.2, 1], delay: 0.1 },
              scale: { duration: 1.25, ease: [0.22, 1, 0.36, 1] },
            }}
            style={{
              background:
                "radial-gradient(circle at 50% 50%, hsl(48 95% 70%) 0%, hsl(48 92% 58%) 45%, hsl(35 90% 52%) 100%)",
              transformOrigin: "50% 50%",
              willChange: "opacity, transform",
            }}
          />
          {/* Soft warm bloom that lingers a beat longer for a filmic afterglow */}
          <motion.div
            key="bloom"
            className="fixed inset-0 z-[81] pointer-events-none"
            initial={{ opacity: 0.6 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: "easeOut", delay: 0.25 }}
            style={{
              background:
                "radial-gradient(60% 45% at 50% 50%, hsl(48 100% 85% / 0.65) 0%, transparent 70%)",
              mixBlendMode: "screen",
            }}
          />
        </>
      )}
    </AnimatePresence>
  );
};